import { useCallback, useEffect, useRef, useState } from "react";
import { getDownloadURL, getStorage, ref as storageRef } from "firebase/storage";
import { getDefaultNextStepsAudioPath } from "../helpers/nextStepsFaqHelpers";

const waitForEvent = (element, eventName, timeoutMs = 2000) =>
  new Promise((resolve) => {
    let timeoutId = null;
    const done = () => {
      if (timeoutId) clearTimeout(timeoutId);
      element.removeEventListener(eventName, done);
      resolve();
    };

    element.addEventListener(eventName, done, { once: true });
    timeoutId = setTimeout(done, timeoutMs);
  });

const isValidSegment = (seg) =>
  Number.isFinite(seg?.start) && Number.isFinite(seg?.end) && seg.end > seg.start;

const getUploadedPath = (config) => {
  const meta = config?.audioMetadata || {};
  if (meta.hasUploadedAudio !== true || !meta.objectPath) return "";
  return String(meta.objectPath).trim();
};

const useNextStepAudio = ({
  podcastId,
  selectedLanguage,
  nextStepsConfig,
  isConfigLoading,
  resourceSegments
}) => {
  const audioRef = useRef(null);
  const playRequestRef = useRef(0);
  const activeResourceKeyRef = useRef(null);
  const [audioUrl, setAudioUrl] = useState("");
  const [activeResourceKey, setActiveResourceKey] = useState(null);
  const [resourceProgress, setResourceProgress] = useState({});
  const [resourceIsPlaying, setResourceIsPlaying] = useState(false);

  const stop = useCallback(() => {
    playRequestRef.current += 1;
    const audio = audioRef.current;
    if (audio) audio.pause();
    setResourceIsPlaying(false);
  }, []);

  const canPlayStep = useCallback(
    (seg) => Boolean(audioUrl) && !isConfigLoading && isValidSegment(seg),
    [audioUrl, isConfigLoading]
  );

  const segmentDuration = useCallback(
    (seg) => (canPlayStep(seg) ? Math.max(0, seg.end - seg.start) : 0),
    [canPlayStep]
  );

  useEffect(() => {
    const audio = audioRef.current;
    playRequestRef.current += 1;
    setAudioUrl("");
    setActiveResourceKey(null);
    activeResourceKeyRef.current = null;
    setResourceProgress({});
    setResourceIsPlaying(false);

    if (audio) {
      audio.pause();
      audio.removeAttribute("src");
      audio.load();
    }

    if (isConfigLoading) return;

    const uploadedPath = getUploadedPath(nextStepsConfig);
    const hasCustomNextSteps = Array.isArray(nextStepsConfig?.nextSteps);
    const isPodcastPage = Boolean(podcastId);
    const path = isPodcastPage
      ? uploadedPath
      : uploadedPath || (hasCustomNextSteps ? "" : getDefaultNextStepsAudioPath(selectedLanguage));

    if (!path) return;

    let cancelled = false;
    const storage = getStorage();
    getDownloadURL(storageRef(storage, path))
      .then((url) => {
        if (!cancelled) setAudioUrl(url);
      })
      .catch((err) => {
        if (isPodcastPage || hasCustomNextSteps) {
          console.error("Next steps audio unavailable for podcast config:", err);
          return;
        }

        if (uploadedPath) {
          getDownloadURL(storageRef(storage, getDefaultNextStepsAudioPath(selectedLanguage)))
            .then((url) => {
              if (!cancelled) setAudioUrl(url);
            })
            .catch((fallbackErr) => console.error("Error fetching fallback next steps audio:", fallbackErr));
          return;
        }

        console.error("Error fetching next steps audio:", err);
      });

    return () => {
      cancelled = true;
    };
  }, [podcastId, selectedLanguage, nextStepsConfig, isConfigLoading]);

  const playStep = useCallback(
    async (seg, opts = {}) => {
      const { resumeIfSame = false } = opts;
      const audio = audioRef.current;
      if (!audio || !canPlayStep(seg)) return;

      const requestId = playRequestRef.current + 1;
      playRequestRef.current = requestId;

      if (resumeIfSame && activeResourceKeyRef.current === seg.key) {
        if (audio.currentTime >= seg.end - 0.05) {
          audio.currentTime = seg.start;
          setResourceProgress((prev) => ({ ...prev, [seg.key]: 0 }));
        }
        try {
          await audio.play();
          if (playRequestRef.current === requestId) setResourceIsPlaying(true);
        } catch (_) {}
        return;
      }

      audio.pause();
      setActiveResourceKey(seg.key);
      activeResourceKeyRef.current = seg.key;
      setResourceProgress((prev) => ({ ...prev, [seg.key]: 0 }));

      if (audio.readyState < 1) {
        await waitForEvent(audio, "loadedmetadata");
      }
      if (playRequestRef.current !== requestId) return;

      audio.currentTime = seg.start;
      await waitForEvent(audio, "seeked", 1500);
      if (playRequestRef.current !== requestId) return;

      try {
        await audio.play();
        if (playRequestRef.current === requestId) setResourceIsPlaying(true);
      } catch (_) {}
    },
    [canPlayStep]
  );

  const seekStep = useCallback(
    async (seg, relSeconds, autoPlay = true) => {
      const audio = audioRef.current;
      if (!audio || !canPlayStep(seg)) return;

      const duration = Math.max(0, seg.end - seg.start);
      const clamped = Math.max(0, Math.min(duration - 0.05, Number(relSeconds) || 0));
      const requestId = playRequestRef.current + 1;
      playRequestRef.current = requestId;

      setActiveResourceKey(seg.key);
      activeResourceKeyRef.current = seg.key;
      if (audio.readyState < 1) {
        await waitForEvent(audio, "loadedmetadata");
      }
      if (playRequestRef.current !== requestId) return;

      audio.currentTime = seg.start + clamped;
      await waitForEvent(audio, "seeked", 1500);
      if (playRequestRef.current !== requestId) return;

      if (autoPlay) {
        try {
          await audio.play();
          if (playRequestRef.current === requestId) setResourceIsPlaying(true);
        } catch (_) {}
      }
      setResourceProgress((prev) => ({ ...prev, [seg.key]: clamped }));
    },
    [canPlayStep]
  );

  useEffect(() => {
    activeResourceKeyRef.current = activeResourceKey;
  }, [activeResourceKey]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onTime = () => {
      const seg = (resourceSegments || []).find((item) => item.key === activeResourceKeyRef.current);
      if (!seg) return;

      if (audio.currentTime >= seg.end) {
        audio.pause();
        audio.currentTime = seg.end;
        setResourceIsPlaying(false);
        return;
      }

      const rel = Math.max(0, audio.currentTime - seg.start);
      const duration = Math.max(0, seg.end - seg.start);
      setResourceProgress((prev) => ({ ...prev, [seg.key]: Math.min(rel, duration) }));
    };

    const onEnded = () => setResourceIsPlaying(false);

    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("ended", onEnded);
    return () => {
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("ended", onEnded);
    };
  }, [resourceSegments]);

  return {
    audioRef,
    audioUrl,
    activeResourceKey,
    resourceProgress,
    resourceIsPlaying,
    setResourceIsPlaying,
    canPlayStep,
    segmentDuration,
    playStep,
    seekStep,
    stop
  };
};

export default useNextStepAudio;
